import React, { useEffect, useState, useRef } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("checking"); // checking | success | failed | timeout
  const [needsApplicants, setNeedsApplicants] = useState(false);
  const attemptsRef = useRef(0);

  // After payment is confirmed, decide where to send the user next.
  useEffect(() => {
    if (status !== "success" || !orderId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: order } = await api.get(`/orders/${orderId}`);
        if (cancelled) return;
        const isCustomWithVisa = order.order_type === "custom" && Array.isArray(order.selected_documents) && order.selected_documents.includes("visa");
        if (isCustomWithVisa) {
          setNeedsApplicants(true);
          // Short delay so the user briefly sees the success state
          setTimeout(() => { if (!cancelled) navigate(`/apply/applicants/${orderId}`); }, 1500);
        }
      } catch (err) {
        // Non-fatal: we can still land the user on /dashboard as the default success view.
        // eslint-disable-next-line no-console
        console.warn("PaymentSuccess: post-payment order lookup failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, [status, orderId, navigate]);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 8) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Header />
      <main className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-lg bg-white border border-[#e2e8f0] rounded-lg p-10 text-center" data-testid="payment-status-card">
          {status === "checking" && (
            <>
              <Loader2 className="w-12 h-12 text-[#2563eb] mx-auto animate-spin" />
              <h1 className="mt-5 font-display font-bold text-2xl text-[#0f1b2d]">Confirming your payment…</h1>
              <p className="mt-2 text-[#475569]">This usually takes a few seconds. Please don&apos;t close the page.</p>
            </>
          )}
          {status === "success" && (
            <>
              <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
              <h1 className="mt-5 font-display font-bold text-2xl text-[#0f1b2d]">Payment received</h1>
              {needsApplicants ? (
                <>
                  <p className="mt-2 text-[#475569]">Taking you to applicant details so we can prepare your Visa Application Form…</p>
                  <Loader2 className="w-6 h-6 text-[#2563eb] mx-auto mt-4 animate-spin" data-testid="post-payment-applicant-redirect" />
                  <Link
                    to={`/apply/applicants/${orderId}`}
                    data-testid="continue-to-applicants"
                    className="mt-4 inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-5 py-2.5 rounded-md"
                  >
                    Continue to applicant details <ArrowRight className="w-4 h-4" />
                  </Link>
                </>
              ) : (
                <>
                  <p className="mt-2 text-[#475569]">Your order is in progress. We&apos;ll prepare your file and notify you on WhatsApp.</p>
                  <Link
                    to="/dashboard"
                    data-testid="go-to-dashboard"
                    className="mt-6 inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-5 py-2.5 rounded-md"
                  >
                    Go to dashboard <ArrowRight className="w-4 h-4" />
                  </Link>
                </>
              )}
            </>
          )}
          {(status === "failed" || status === "timeout") && (
            <>
              <XCircle className="w-14 h-14 text-red-600 mx-auto" />
              <h1 className="mt-5 font-display font-bold text-2xl text-[#0f1b2d]">{status === "timeout" ? "Still processing…" : "Payment not confirmed"}</h1>
              <p className="mt-2 text-[#475569]">{status === "timeout" ? "Check your dashboard in a moment — we'll update it as soon as payment is confirmed." : "We could not confirm your payment. If your card was charged, contact support."}</p>
              <div className="mt-6 flex items-center gap-3 justify-center">
                <Link to="/dashboard" className="inline-flex items-center gap-2 border border-[#e2e8f0] text-[#0f1b2d] font-medium px-5 py-2.5 rounded-md">Go to dashboard</Link>
                <Link to="/apply" className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-5 py-2.5 rounded-md">Try again</Link>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
